/**
 * Operational Hours Configuration Modal
 * Configure operational hours and alert delays for locations
 */

import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  TextField,
  Autocomplete,
  FormControlLabel,
  Switch,
  Slider,
} from "@mui/material";

import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import useApiRequest from "utils/Apirequest";
import { useMaterialUIController } from "context";

const OperationalHoursModal = ({
  open,
  onClose,
  locationId,
  config,
  onSave,
}) => {
  const apiRequest = useApiRequest();
  const [controller] = useMaterialUIController();
  const { darkMode, sidenavColor } = controller;

  const [formData, setFormData] = useState({
    enabled: false,
    startTime: "00:00",
    endTime: "23:59",
    timezone: "Asia/Dubai",
    alertDelay: 15,
  });

  useEffect(() => {
    if (config) {
      setFormData(config);
    }
  }, [config]);

  const timezones = [
    "Asia/Dubai",
    "Asia/Kolkata",
    "Asia/Riyadh",
    "Europe/London",
    "America/New_York",
    "Asia/Singapore",
  ];

  const handleSave = async () => {
    try {
      const endpoint = `locations/${locationId}/operational-hours`;
      const { data } = await apiRequest("put", endpoint, formData, {}, {});

      if (data?.success) {
        onSave();
        onClose();
      }
    } catch (error) {
      console.error("Error saving operational hours:", error);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: darkMode ? "#1a2035" : "#fff",
        },
      }}
    >
      <DialogTitle>
        <MDTypography variant="h5" fontWeight="medium">
          Configure Operational Hours
        </MDTypography>
      </DialogTitle>

      <DialogContent>
        <MDBox pt={2}>
          <Grid container spacing={3}>
            {/* Enable/Disable */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={formData.enabled}
                    onChange={(e) =>
                      setFormData({ ...formData, enabled: e.target.checked })
                    }
                    color="primary"
                  />
                }
                label={
                  <MDTypography variant="button" fontWeight="medium">
                    Enable Operational Hours Check
                  </MDTypography>
                }
              />
              <MDTypography
                variant="caption"
                color="text"
                display="block"
                mt={1}
              >
                When enabled, fault notifications will only be sent during
                operational hours
              </MDTypography>
            </Grid>

            {/* Start Time */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Start Time"
                type="time"
                value={formData.startTime}
                onChange={(e) =>
                  setFormData({ ...formData, startTime: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={!formData.enabled}
              />
            </Grid>

            {/* End Time */}
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="End Time"
                type="time"
                value={formData.endTime}
                onChange={(e) =>
                  setFormData({ ...formData, endTime: e.target.value })
                }
                InputLabelProps={{
                  shrink: true,
                }}
                disabled={!formData.enabled}
              />
            </Grid>

            {/* Timezone */}
            <Grid item xs={12}>
              <Autocomplete
                disabled={!formData.enabled}
                options={timezones}
                value={formData.timezone || null}
                onChange={(_, newValue) =>
                  setFormData({ ...formData, timezone: newValue })
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Timezone"
                    placeholder="Search timezone"
                    helperText={
                      formData.enabled
                        ? "Timezone used to evaluate operational hours"
                        : "Enable operational hours to change timezone"
                    }
                    fullWidth
                  />
                )}
              />
            </Grid>

            {/* Alert Delay */}
            <Grid item xs={12}>
              <MDBox
                p={2}
                borderRadius="lg"
                sx={{
                  border: "1px solid",
                  borderColor: darkMode
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.08)",
                  opacity: formData.enabled ? 1 : 0.5,
                }}
              >
                <MDBox display="flex" justifyContent="space-between" mb={1}>
                  <MDTypography variant="button" fontWeight="medium">
                    Alert Delay
                  </MDTypography>
                  <MDTypography
                    variant="button"
                    color="primary"
                    fontWeight="bold"
                  >
                    {formData.alertDelay} min
                  </MDTypography>
                </MDBox>

                <MDTypography
                  variant="caption"
                  color="text"
                  mb={2}
                  display="block"
                >
                  Delay before sending fault notifications to avoid false alarms
                </MDTypography>

                <Slider
                  value={formData.alertDelay}
                  onChange={(_, newValue) =>
                    setFormData({ ...formData, alertDelay: newValue })
                  }
                  min={5}
                  max={60}
                  step={5}
                  marks={[
                    { value: 5, label: "5m" },
                    { value: 15, label: "15m" },
                    { value: 30, label: "30m" },
                    { value: 60, label: "60m" },
                  ]}
                  valueLabelDisplay="auto"
                  disabled={!formData.enabled}
                  sx={{
                    mt: 1,
                    "& .MuiSlider-thumb": {
                      border: "2px solid white",
                    },
                  }}
                />
              </MDBox>
            </Grid>

            {/* Configuration Summary */}
            <Grid item xs={12}>
              <MDBox
                p={2.5}
                borderRadius="lg"
                sx={{
                  background: darkMode
                    ? "linear-gradient(145deg, rgba(255,255,255,0.05), rgba(255,255,255,0.02))"
                    : "linear-gradient(145deg, #f9fafb, #ffffff)",
                  border: "1px solid",
                  borderColor: darkMode
                    ? "rgba(255,255,255,0.12)"
                    : "rgba(0,0,0,0.08)",
                }}
              >
                <MDTypography variant="button" fontWeight="bold" mb={1}>
                  Configuration Summary
                </MDTypography>

                <MDBox display="flex" flexDirection="column" gap={0.5}>
                  <MDTypography variant="caption">
                    <strong>Status:</strong>{" "}
                    <span
                      style={{
                        color: formData.enabled ? "#2e7d32" : "#9e9e9e",
                      }}
                    >
                      {formData.enabled ? "Enabled" : "Disabled"}
                    </span>
                  </MDTypography>

                  {formData.enabled && (
                    <>
                      <MDTypography variant="caption">
                        <strong>Operating Hours:</strong> {formData.startTime} –{" "}
                        {formData.endTime}
                      </MDTypography>

                      <MDTypography variant="caption">
                        <strong>Timezone:</strong> {formData.timezone}
                      </MDTypography>

                      <MDTypography variant="caption">
                        <strong>Alert Delay:</strong> {formData.alertDelay}{" "}
                        minutes
                      </MDTypography>
                    </>
                  )}
                </MDBox>
              </MDBox>
            </Grid>
          </Grid>
        </MDBox>
      </DialogContent>

      <DialogActions>
        <MDButton onClick={onClose} color="secondary">
          Cancel
        </MDButton>
        <MDButton onClick={handleSave} variant="gradient" color={sidenavColor}>
          Save Configuration
        </MDButton>
      </DialogActions>
    </Dialog>
  );
};

export default OperationalHoursModal;
